import reduce from 'lodash/reduce'
import * as React from 'react'
import {H3} from '@frontrow/console/src/components/typography'
import {
  type SyncResultSuccessT,
  type SnapshotCountsT
} from '@frontrow/console/src/entities/models/roster-sync'
import {type ColumnDef} from '@tanstack/react-table'

import DataTable from '@frontrow/console/src/components/common/data-table'
import * as style from './sync-success.module.scss'

type Props = {
  successes: Array<SyncResultSuccessT>
}

export default function RosterSyncSuccess(props: Props): React.ReactElement {
  const {successes} = props
  const sumStats = sumResults(successes)
  return (
    <React.Fragment>
      <div>
        <div className={style.overview}>
          <H3>Teachers</H3>
          <p>
            {sumStats.teachers.addedCount} New,&nbsp;
            {sumStats.teachers.updatedCount} Updated,&nbsp;
            {sumStats.teachers.unchangedCount} Unchanged,&nbsp;
            {sumStats.teachers.deletedCount} Deleted
          </p>
        </div>
        <div className={style.overview}>
          <H3>Students</H3>
          <p>
            {sumStats.students.addedCount} New,&nbsp;
            {sumStats.students.updatedCount} Updated,&nbsp;
            {sumStats.students.unchangedCount} Unchanged,&nbsp;
            {sumStats.students.deletedCount} Deleted
          </p>
        </div>
      </div>
      <DataTable
        isLoading={false}
        updateUrlOnFilter={false}
        data={successes}
        columns={[
          {
            id: 'school-id',
            header: 'School ID',
            accessorFn: (stats: SyncResultSuccessT) => stats.schoolIdpId,
            enableSorting: true,
            enableColumnFilter: true
          },
          {
            id: 'school-name',
            header: 'School Name',
            accessorFn: (stats: SyncResultSuccessT) => stats.schoolName,
            enableSorting: true,
            enableColumnFilter: true
          },
          ...dataTableEntityColumns('Teachers', 'teacherCount'),
          ...dataTableEntityColumns('Students', 'studentCount'),
          ...dataTableEntityColumns('Courses', 'courseCount'),
          ...dataTableEntityColumns('Memberships', 'courseMembershipCount')
        ]}
      />
    </React.Fragment>
  )
}

type SuccessSumT = {
  unchangedCount: number
  updatedCount: number
  deletedCount: number
  addedCount: number
}

function sumResults(successes: Array<SyncResultSuccessT>): {
  teachers: SuccessSumT
  students: SuccessSumT
} {
  const empty = {
    unchangedCount: 0,
    updatedCount: 0,
    deletedCount: 0,
    addedCount: 0
  }
  const sum = (property: keyof SnapshotCountsT, acc: SuccessSumT, x: SyncResultSuccessT) => ({
    unchangedCount: acc.unchangedCount + x.unchangedCounts[property],
    updatedCount: acc.updatedCount + x.updatedCounts[property],
    deletedCount: acc.deletedCount + x.deletedCounts[property],
    addedCount: acc.addedCount + x.addedCounts[property]
  })
  return {
    teachers: reduce(successes, sum.bind({}, 'teacherCount'), empty),
    students: reduce(successes, sum.bind({}, 'studentCount'), empty)
  }
}

function dataTableEntityColumns(
  header: string,
  accessor: keyof SnapshotCountsT
): ColumnDef<SyncResultSuccessT, unknown>[] {
  const hideZero = (x: number) => (x === 0 ? '' : x)
  return [
    {
      id: `${header}-created`,
      header: `${header} Created`,
      accessorFn: stats => hideZero(stats.addedCounts[accessor])
    },
    {
      id: `${header}-updated`,
      header: `${header} Updated`,
      accessorFn: stats => hideZero(stats.updatedCounts[accessor])
    },
    {
      id: `${header}-unchanged`,
      header: `${header} Unchanged`,
      accessorFn: stats => hideZero(stats.unchangedCounts[accessor])
    },
    {
      id: `${header}-deleted`,
      header: `${header} Deleted`,
      accessorFn: stats => hideZero(stats.deletedCounts[accessor])
    }
  ]
}
